import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects'
import { loadDefaultOptions, loadOpciones } from '../../api/selector'
import {
  InStepDetails,
  Step,
  StepDetails,
} from '../../components/FunnelSelector/SelectorSteps'
import { reportSelectorSteps } from '../../utils/gtmSelectorReporter'
import route from '../../utils/route'
import * as RouteActions from '../actions/routeActions'
import { search } from '../actions/searchActions'
import * as SelectorActions from '../actions/selectorActions'
import { SelectorState } from '../reducers/selectorReducer'
import {
  HOME_SELECTOR_PLACEHOLDER_MEDIDAS,
} from '../../components/FunnelSelector/hooks/useSyncedMedidasSelectorFields'
import {
  selectMedidas,
  selectRunflat,
  selectSelector,
  selectTipoVehiculo,
} from '../selectors/selectorSelectors'
import { navigate } from 'gatsby'
import {
  setMedidasIniciales,
  syncLegacyFunnelMedidasToCatalog,
} from '../../../coche/Neumaticos/Buscador/SeleccionMedidas/Infrastructure/store/medidasSlice'

function buildFallbackSelectorDefaults(): Partial<SelectorState> {
  const field = (name: keyof typeof HOME_SELECTOR_PLACEHOLDER_MEDIDAS) => {
    const value = HOME_SELECTOR_PLACEHOLDER_MEDIDAS[name]
    return {
      loading: false,
      value,
      default: value,
      options: [{ label: String(value), value }],
    }
  }
  return {
    pristine: true,
    ancho: field('ancho'),
    serie: field('serie'),
    llanta: field('llanta'),
    carga: field('carga'),
    velocidad: field('velocidad'),
  }
}

function* fetchMedidas() {
  const state = yield select()
  const tipoVehiculo = selectTipoVehiculo(state)
  const medidas = selectMedidas(state)

  // SelectorModal needs to restore the state before opening when the
  // modal is closed without searching again. It does this by dispatching
  // a RESTORE action.
  // Here we need to race between that possible RESTORE and any loadOpciones
  // execution, because otherwise the selector may RESTORE the previous state
  // and only later a running execution of loadOpciones terminates, loading
  // the options for that (now cancelled) selection and messing everything up.
  // The race here cancels any in-progres loadOpciones, averting that crysis.
  const { opciones } = yield race({
    opciones: call(loadOpciones, medidas, selectRunflat(state), tipoVehiculo),
    cancelled: take(SelectorActions.RESTORE),
  })
  if (opciones) {
    const _opts = opciones as Partial<SelectorState> | null
    const medida = Object.keys(_opts)
      .filter((o) => o !== 'runflat')
      .find((m) => _opts[m].value === null)

    if (medida && _opts[medida].options) {
      const selectorState = selectSelector(state)
      const dfault = selectorState[medida].default || 0
      if (_opts[medida].options.find((o) => o.value === dfault)) {
        _opts[medida].default = selectSelector(state)[medida].default
      }
    }
    yield put(SelectorActions.loadOptions(_opts))
  }
}

function* selectMedidasNeumatico(
  action: ReturnType<typeof SelectorActions.selectFeaturesFromTyre>
) {
  const neumatico = action.payload.neumatico
  const current = selectSelector(yield select())

  // No passar per fetchMedidas/loadOpciones: resolveMedidaValueForOptions
  // pot deixar value=null si la mida del vehicle no és a la llista de l'API,
  // i aleshores es veuen els defaults.
  const numField = (raw: string | number) => {
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10)
    const value = Number.isFinite(n) ? n : raw
    return {
      loading: false,
      value,
      default: null,
      options: [{ label: String(value), value }],
    }
  }
  const strField = (raw: string | number) => {
    const value = String(raw)
    return {
      loading: false,
      value,
      default: null,
      options: [{ label: value, value }],
    }
  }

  let temporada = current.temporada
  if (neumatico.allseason) {
    temporada = { ...temporada, value: 'allseason' }
  } else if (neumatico.invierno) {
    temporada = { ...temporada, value: 'invierno' }
  } else if (neumatico.verano) {
    temporada = { ...temporada, value: 'verano' }
  }

  yield put(
    SelectorActions.loadOptions({
      pristine: false,
      origen: current.origen,
      temporada,
      runflat: {
        ...current.runflat,
        value: Boolean(neumatico.runflat),
      },
      ancho: numField(neumatico.ancho),
      serie: numField(neumatico.serie),
      llanta: numField(neumatico.llanta),
      carga: numField(neumatico.ic),
      velocidad: strField(neumatico.cv),
    })
  )

  yield put(syncLegacyFunnelMedidasToCatalog({ force: true }))
  yield race({
    synced: take(setMedidasIniciales.type),
    timeout: delay(300),
  })
  navigate(StepDetails[Step.INITIAL].routes.desktop)
}

function* fetchDefaults(action: ReturnType<typeof SelectorActions.reset>) {
  try {
    const defaults = yield call(
      loadDefaultOptions,
      action.payload.tipoVehiculo
    )
    if (action.payload.keepValues) {
      action.payload.keepMedidas.forEach((m) => {
        defaults[m.name].value = m.value
      })
    }

    // Area-privada / canvi neumàtic: no trepitjar una selecció real (pristine=false).
    const selectorState = selectSelector(yield select())
    if (!selectorState.pristine) {
      return
    }

    yield put({
      type: SelectorActions.RESET_OK,
      payload: defaults,
    })
  } catch {
    const selectorState = selectSelector(yield select())
    if (!selectorState.pristine) {
      return
    }
    yield put({
      type: SelectorActions.RESET_OK,
      payload: buildFallbackSelectorDefaults(),
    })
  }
}

function normalizePathname(pathname: string | undefined): string {
  if (!pathname) return ''
  const trimmed = pathname.replace(/\/+$/, '')
  return trimmed || '/'
}

function pathStartsWith(pathname: string | undefined, base: string): boolean {
  const p = normalizePathname(pathname)
  const b = normalizePathname(base)
  if (!b || b === '/') return p === b
  return p === b || p.startsWith(`${b}/`)
}

function* maybeUpdateSearch(
  action: ReturnType<typeof RouteActions.onPreRoute>
) {
  const todosPath = route('selector.resultados.todos')
  const { from, to } = action.payload

  const enteringTodos =
    pathStartsWith(to?.pathname, todosPath) &&
    !pathStartsWith(from?.pathname, todosPath)

  if (enteringTodos) {
    yield put(search())
  }
}

interface Action {
  type: 'SELECTOR::REPORT_STEP'
  payload: {
    step: InStepDetails
    device: 'mobile' | 'desktop'
  }
}
function* reportSteps(action: Action) {
  const state = yield select()
  const selectorState = selectSelector(state)
  reportSelectorSteps(
    selectorState,
    action.payload.device,
    action.payload.step.step,
    selectorState.marcas
  )
}

function* sagas() {
  return all([
    yield takeLatest(SelectorActions.SELECT_MEDIDA, fetchMedidas),
    yield takeLatest(
      SelectorActions.SELECT_MEDIDAS_NEUMATICO,
      selectMedidasNeumatico
    ),
    yield takeLatest(SelectorActions.RESET, fetchDefaults),
    yield takeLatest(RouteActions.PRE_ROUTE, maybeUpdateSearch),
    yield takeLatest(SelectorActions.REPORT_STEP, reportSteps),
  ])
}

export default sagas
